import React, { useState, useEffect, useMemo } from 'react';
import { getVisitorLogs } from '../../services/api';
import { 
    Eye, Users, UserCheck, UserX, Calendar, RefreshCw, 
    Smartphone, Monitor, Globe, ChevronDown, ChevronUp, 
    Search, Layers, List, Clock, Activity
} from 'lucide-react';

const VisitorLogs = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    const [selectedDate, setSelectedDate] = useState(todayStr);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({
        total_visits: 0,
        logged_in_visits: 0,
        guest_visits: 0,
        unique_logged_in_users: 0,
        unique_ips: 0
    });
    const [loading, setLoading] = useState(true);

    // View Mode: 'grouped' (default aggregated user dropdowns) or 'timeline' (flat stream)
    const [viewMode, setViewMode] = useState('grouped');
    
    // Filters & Search
    const [searchQuery, setSearchQuery] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'user', 'guest'

    // Expanded Visitor Accordion state (Set of keys)
    const [expandedVisitors, setExpandedVisitors] = useState(new Set());

    const fetchLogs = async (dateVal = selectedDate) => {
        setLoading(true);
        try {
            const res = await getVisitorLogs(dateVal);
            if (res.success) {
                setLogs(res.data || []);
                setStats(res.stats || {});
            }
        } catch (error) {
            console.error("Failed to load visitor logs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs(selectedDate);
    }, [selectedDate]);

    const toggleVisitorExpand = (key) => {
        setExpandedVisitors(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const expandAll = () => {
        const allKeys = new Set(filteredGroupedVisitors.map(v => v.key));
        setExpandedVisitors(allKeys);
    };

    const collapseAll = () => {
        setExpandedVisitors(new Set());
    };

    const formatDateTime = (isoStr) => {
        if (!isoStr) return 'N/A';
        try {
            const d = new Date(isoStr);
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return isoStr;
        }
    };

    // Aggregate logs by Visitor Identity
    const groupedVisitors = useMemo(() => {
        const map = new Map();

        logs.forEach(log => {
            const key = log.user_id 
                ? `user_${log.user_id}` 
                : log.user_email 
                ? `email_${log.user_email}` 
                : `ip_${log.ip_address}`;

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    isUser: !!log.user_id,
                    user_id: log.user_id,
                    user_name: log.user_name || (log.user_id ? `Student #${log.user_id}` : 'Guest Visitor'),
                    user_email: log.user_email || '',
                    ip_address: log.ip_address || '127.0.0.1',
                    devices: new Set([log.device_type || 'Desktop']),
                    totalVisits: 0,
                    lastVisitedAt: log.visited_at,
                    pageCounts: {},
                    history: []
                });
            }

            const visitor = map.get(key);
            visitor.totalVisits += 1;
            if (log.device_type) visitor.devices.add(log.device_type);

            const pageKey = log.page_name || 'Unknown Page';
            if (!visitor.pageCounts[pageKey]) {
                visitor.pageCounts[pageKey] = {
                    page_name: pageKey,
                    page_url: log.page_url || '/',
                    count: 0
                };
            }
            visitor.pageCounts[pageKey].count += 1;

            visitor.history.push(log);
        });

        return Array.from(map.values()).sort((a, b) => {
            return new Date(b.lastVisitedAt) - new Date(a.lastVisitedAt);
        });
    }, [logs]);

    // Filter Grouped Visitors by Search & User Type
    const filteredGroupedVisitors = useMemo(() => {
        return groupedVisitors.filter(v => {
            if (userTypeFilter === 'user' && !v.isUser) return false;
            if (userTypeFilter === 'guest' && v.isUser) return false;

            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchesName = v.user_name.toLowerCase().includes(q);
                const matchesEmail = v.user_email.toLowerCase().includes(q);
                const matchesIP = v.ip_address.toLowerCase().includes(q);
                const matchesPages = Object.keys(v.pageCounts).some(p => p.toLowerCase().includes(q));
                return matchesName || matchesEmail || matchesIP || matchesPages;
            }

            return true;
        });
    }, [groupedVisitors, searchQuery, userTypeFilter]);

    // Filter Raw Logs for Timeline View
    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const isUser = !!log.user_id;
            if (userTypeFilter === 'user' && !isUser) return false;
            if (userTypeFilter === 'guest' && isUser) return false;

            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchesName = (log.user_name || '').toLowerCase().includes(q);
                const matchesEmail = (log.user_email || '').toLowerCase().includes(q);
                const matchesIP = (log.ip_address || '').toLowerCase().includes(q);
                const matchesPage = (log.page_name || '').toLowerCase().includes(q);
                const matchesUrl = (log.page_url || '').toLowerCase().includes(q);
                return matchesName || matchesEmail || matchesIP || matchesPage || matchesUrl;
            }

            return true;
        });
    }, [logs, searchQuery, userTypeFilter]);

    return (
        <div className="p-2 sm:p-4 max-w-7xl mx-auto space-y-3 font-sans text-xs">
                
            {/* Header & Date Controls Bar */}
            <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <div>
                    <div className="flex items-center gap-1.5 text-emerald-600 font-bold text-[11px] uppercase tracking-wide">
                        <Activity size={13} className="animate-pulse" /> Visitor Analytics
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        Track live page views, user visits, and granular activity history.
                    </p>
                </div>

                {/* Date Controls */}
                <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                    <div className="relative flex items-center">
                        <Calendar size={13} className="absolute left-2.5 text-gray-400 pointer-events-none" />
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="pl-7 pr-2 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-[11px] font-semibold text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                        />
                    </div>

                    <button
                        onClick={() => setSelectedDate(todayStr)}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition-all ${selectedDate === todayStr ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100'}`}
                    >
                        Today
                    </button>

                    <button
                        onClick={() => setSelectedDate('')}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition-all ${!selectedDate ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100'}`}
                    >
                        All Time
                    </button>

                    <button
                        onClick={() => fetchLogs(selectedDate)}
                        className="p-1.5 bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors"
                        title="Refresh Data"
                    >
                        <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                    </button>
                </div>
            </div>

            {/* KPI Summary Stat Cards (2x2 Grid on Mobile, 4-Cols on Desktop) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Views</p>
                        <h3 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white mt-0.5">
                            {(stats.total_visits || 0).toLocaleString()}
                        </h3>
                        <p className="text-[10px] text-gray-400">Page hits</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 flex items-center justify-center shrink-0">
                        <Eye size={16} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Students</p>
                        <h3 className="text-lg sm:text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
                            {(stats.logged_in_visits || 0).toLocaleString()}
                        </h3>
                        <p className="text-[10px] text-emerald-600/80 font-medium">{stats.unique_logged_in_users || 0} unique</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 flex items-center justify-center shrink-0">
                        <UserCheck size={16} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Guests</p>
                        <h3 className="text-lg sm:text-xl font-black text-amber-500 dark:text-amber-400 mt-0.5">
                            {(stats.guest_visits || 0).toLocaleString()}
                        </h3>
                        <p className="text-[10px] text-amber-600/80 font-medium">Anonymous</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950/60 text-amber-500 flex items-center justify-center shrink-0">
                        <UserX size={16} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Unique IPs</p>
                        <h3 className="text-lg sm:text-xl font-black text-blue-600 dark:text-blue-400 mt-0.5">
                            {(stats.unique_ips || 0).toLocaleString()}
                        </h3>
                        <p className="text-[10px] text-blue-600/80 font-medium">Devices</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/60 text-blue-600 flex items-center justify-center shrink-0">
                        <Globe size={16} />
                    </div>
                </div>
            </div>

            {/* Filter & View Mode Control Bar */}
            <div className="bg-white dark:bg-gray-800 p-2.5 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                
                {/* View Mode Toggle */}
                <div className="flex items-center p-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg shrink-0 text-xs">
                    <button
                        onClick={() => setViewMode('grouped')}
                        className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md transition-all ${viewMode === 'grouped' ? 'bg-white dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 shadow-xs' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800'}`}
                    >
                        <Layers size={13} /> Grouped ({groupedVisitors.length})
                    </button>

                    <button
                        onClick={() => setViewMode('timeline')}
                        className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md transition-all ${viewMode === 'timeline' ? 'bg-white dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 shadow-xs' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800'}`}
                    >
                        <List size={13} /> Activity Stream ({logs.length})
                    </button>
                </div>

                {/* Search & Filters */}
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {/* Compact Search Bar */}
                    <div className="relative flex-1 sm:w-48">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search visitor, IP, page..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-7 pr-2.5 py-1 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-lg text-[11px] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                    </div>

                    {/* Type Filter */}
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-lg p-0.5 text-[11px]">
                        <button
                            onClick={() => setUserTypeFilter('all')}
                            className={`px-2 py-0.5 font-bold rounded ${userTypeFilter === 'all' ? 'bg-white dark:bg-gray-800 text-emerald-600 shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setUserTypeFilter('user')}
                            className={`px-2 py-0.5 font-bold rounded ${userTypeFilter === 'user' ? 'bg-emerald-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            Students
                        </button>
                        <button
                            onClick={() => setUserTypeFilter('guest')}
                            className={`px-2 py-0.5 font-bold rounded ${userTypeFilter === 'guest' ? 'bg-amber-500 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            Guests
                        </button>
                    </div>

                    {/* Expand/Collapse All */}
                    {viewMode === 'grouped' && filteredGroupedVisitors.length > 0 && (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={expandAll}
                                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 text-[10px] font-bold rounded transition-all"
                            >
                                Expand All
                            </button>
                            <button
                                onClick={collapseAll}
                                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 text-[10px] font-bold rounded transition-all"
                            >
                                Collapse
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            {loading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-10 text-center text-gray-400 flex flex-col items-center gap-2 border border-gray-100 dark:border-gray-700">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
                    <p className="text-xs font-bold text-gray-700 dark:text-gray-200">Loading Visitor Analytics...</p>
                </div>
            ) : logs.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-10 text-center text-gray-400 space-y-2 border border-gray-100 dark:border-gray-700">
                    <Users className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
                    <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200">No Visitor Logs Recorded</h3>
                    <button
                        onClick={() => { setSelectedDate(''); setSearchQuery(''); setUserTypeFilter('all'); }}
                        className="px-3 py-1 bg-emerald-600 text-white font-bold text-[11px] rounded-lg shadow-xs hover:bg-emerald-700 transition-all mt-1"
                    >
                        Clear Filters
                    </button>
                </div>
            ) : viewMode === 'grouped' ? (
                /* COMPACT GROUPED VISITOR CARDS (ACCORDION DROPDOWN SYSTEM) */
                <div className="space-y-2">
                    <div className="flex items-center justify-between text-[11px] text-gray-400 font-medium px-1">
                        <span>Showing {filteredGroupedVisitors.length} visitor profiles</span>
                        <span className="hidden sm:inline">Click any row to open visit details dropdown</span>
                    </div>

                    {filteredGroupedVisitors.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 p-5 text-center rounded-xl border border-gray-100 dark:border-gray-700 text-gray-400 text-xs">
                            No visitors match "{searchQuery}".
                        </div>
                    ) : (
                        filteredGroupedVisitors.map((visitor) => {
                            const isExpanded = expandedVisitors.has(visitor.key);
                            const pagesList = Object.values(visitor.pageCounts).sort((a, b) => b.count - a.count);
                            const deviceArray = Array.from(visitor.devices);

                            return (
                                <div 
                                    key={visitor.key}
                                    className={`bg-white dark:bg-gray-800 rounded-xl border transition-all overflow-hidden ${isExpanded ? 'border-emerald-500 dark:border-emerald-500 shadow-xs ring-1 ring-emerald-500/20' : 'border-gray-100 dark:border-gray-700/80 hover:border-gray-300 dark:hover:border-gray-600'}`}
                                >
                                    {/* COMPACT VISITOR ROW HEADER (Clickable) */}
                                    <div 
                                        onClick={() => toggleVisitorExpand(visitor.key)}
                                        className="p-2.5 sm:p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-750/50 transition-colors select-none"
                                    >
                                        {/* Left Info: Avatar, Name, Email, Visit Badge */}
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs shadow-xs shrink-0 ${visitor.isUser ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white' : 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'}`}>
                                                {visitor.isUser ? (
                                                    (visitor.user_name || 'U').charAt(0).toUpperCase()
                                                ) : (
                                                    <UserX size={15} />
                                                )}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <h3 className="font-bold text-xs text-gray-900 dark:text-white truncate">
                                                        {visitor.user_name}
                                                    </h3>

                                                    {/* Compact Visit Badge */}
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black border ${visitor.isUser ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800' : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800'}`}>
                                                        🔥 {visitor.totalVisits} {visitor.totalVisits === 1 ? 'visit' : 'visits'}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
                                                    {visitor.user_email && (
                                                        <span className="font-medium text-gray-600 dark:text-gray-300 truncate max-w-[180px]">
                                                            {visitor.user_email}
                                                        </span>
                                                    )}
                                                    <span className="font-mono text-gray-400 text-[10px]">
                                                        IP: {visitor.ip_address}
                                                    </span>
                                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium text-[10px] flex items-center gap-0.5">
                                                        <Clock size={10} /> {formatDateTime(visitor.lastVisitedAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Meta: Device badges & Dropdown Trigger Button */}
                                        <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-gray-700">
                                            {/* Devices */}
                                            <div className="flex items-center gap-1">
                                                {deviceArray.map(dev => (
                                                    <span 
                                                        key={dev}
                                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${dev === 'Mobile' ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-800' : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800'}`}
                                                    >
                                                        {dev === 'Mobile' ? <Smartphone size={10} /> : <Monitor size={10} />}
                                                        {dev}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Compact Dropdown Trigger Button */}
                                            <button 
                                                className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg border transition-all ${isExpanded ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600'}`}
                                            >
                                                <span>{isExpanded ? 'Hide' : `View ${visitor.totalVisits}`}</span>
                                                {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* EXPANDABLE DROPDOWN DETAILS CONTAINER */}
                                    {isExpanded && (
                                        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/60 p-3 space-y-3">
                                            
                                            {/* Page Visit Frequency Summary */}
                                            <div>
                                                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1">
                                                    <Layers size={12} className="text-emerald-600" /> Visited Pages Summary
                                                </h4>
                                                
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5">
                                                    {pagesList.map(page => (
                                                        <div 
                                                            key={page.page_name}
                                                            className="bg-white dark:bg-gray-800 p-2 rounded-lg border border-gray-200/70 dark:border-gray-700 flex items-center justify-between gap-2"
                                                        >
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-[11px] font-bold text-gray-900 dark:text-white truncate" title={page.page_name}>
                                                                    {page.page_name}
                                                                </p>
                                                                <p className="text-[10px] font-mono text-gray-400 truncate" title={page.page_url}>
                                                                    {page.page_url}
                                                                </p>
                                                            </div>
                                                            <div className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded text-[10px] font-black shrink-0">
                                                                {page.count}x
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Chronological Visit Log Table */}
                                            <div>
                                                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1">
                                                    <Clock size={12} className="text-emerald-600" /> Complete History ({visitor.totalVisits} visits)
                                                </h4>

                                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                    <div className="max-h-60 overflow-y-auto overflow-x-auto">
                                                        <table className="w-full text-left border-collapse min-w-[500px]">
                                                            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-750 text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase border-b border-gray-200 dark:border-gray-700">
                                                                <tr>
                                                                    <th className="py-1.5 px-3">#</th>
                                                                    <th className="py-1.5 px-3">Date & Time</th>
                                                                    <th className="py-1.5 px-3">Page Name & Path</th>
                                                                    <th className="py-1.5 px-3">Device</th>
                                                                    <th className="py-1.5 px-3">IP Address</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-[11px]">
                                                                {visitor.history.map((log, idx) => (
                                                                    <tr key={log.id || idx} className="hover:bg-gray-50/80 dark:hover:bg-gray-750/50">
                                                                        <td className="py-1.5 px-3 font-mono font-bold text-gray-400 text-[10px]">
                                                                            {visitor.history.length - idx}
                                                                        </td>
                                                                        <td className="py-1.5 px-3 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                                                            {formatDateTime(log.visited_at)}
                                                                        </td>
                                                                        <td className="py-1.5 px-3">
                                                                            <span className="font-bold text-gray-900 dark:text-white">
                                                                                {log.page_name}
                                                                            </span>
                                                                            <span className="ml-2 font-mono text-[10px] text-gray-400">
                                                                                {log.page_url}
                                                                            </span>
                                                                        </td>
                                                                        <td className="py-1.5 px-3">
                                                                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-bold ${log.device_type === 'Mobile' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                                                                {log.device_type || 'Desktop'}
                                                                            </span>
                                                                        </td>
                                                                        <td className="py-1.5 px-3 font-mono text-gray-500 text-[10px]">
                                                                            {log.ip_address || '127.0.0.1'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                /* COMPACT TIMELINE LOG STREAM VIEW */
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                        <h2 className="text-xs font-extrabold text-gray-900 dark:text-white">Log Stream</h2>
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 rounded border border-emerald-200 dark:border-emerald-800">
                            {filteredLogs.length} entries
                        </span>
                    </div>

                    {filteredLogs.length === 0 ? (
                        <div className="p-8 text-center text-gray-400 text-xs">
                            No logs match "{searchQuery}".
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[600px]">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-750 text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase border-b border-gray-100 dark:border-gray-700">
                                        <th className="py-2 px-3">Date & Time</th>
                                        <th className="py-2 px-3">Visitor Identity</th>
                                        <th className="py-2 px-3">Page Visited</th>
                                        <th className="py-2 px-3">Device</th>
                                        <th className="py-2 px-3">IP Address</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-[11px]">
                                    {filteredLogs.map((log) => {
                                        const isUser = !!log.user_id;
                                        return (
                                            <tr key={log.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-750/50 transition-colors">
                                                <td className="py-2 px-3 font-medium text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                                    {formatDateTime(log.visited_at)}
                                                </td>

                                                <td className="py-2 px-3">
                                                    {isUser ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded bg-emerald-600 text-white font-bold text-[10px] flex items-center justify-center shrink-0">
                                                                {(log.user_name || 'U').charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="font-bold text-emerald-700 dark:text-emerald-400 leading-tight truncate">
                                                                    {log.user_name || 'Student'}
                                                                </p>
                                                                <p className="text-[10px] text-gray-400 truncate">
                                                                    {log.user_email || `#${log.user_id}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 text-[10px] font-bold rounded">
                                                            <UserX size={11} /> Guest
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="py-2 px-3">
                                                    <div className="max-w-xs truncate">
                                                        <p className="font-semibold text-gray-900 dark:text-gray-100 truncate" title={log.page_name}>
                                                            {log.page_name}
                                                        </p>
                                                        <p className="text-[10px] text-gray-400 font-mono truncate" title={log.page_url}>
                                                            {log.page_url}
                                                        </p>
                                                    </div>
                                                </td>

                                                <td className="py-2 px-3">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${log.device_type === 'Mobile' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                                                        {log.device_type === 'Mobile' ? <Smartphone size={10} /> : <Monitor size={10} />}
                                                        {log.device_type || 'Desktop'}
                                                    </span>
                                                </td>

                                                <td className="py-2 px-3 font-mono text-[10px] text-gray-500">
                                                    {log.ip_address || '127.0.0.1'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
};

export default VisitorLogs;
